import React, { useCallback, useEffect } from 'react';
import classnames from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import { useWizard } from 'ui-components/components/wizard/context';

import { TermLink } from '../../../../components/term-link';
import { AcceptTerms } from '../../../../components/accept-terms';
import { Versions } from '../../../../../services/link-service';
import { messages } from '../../../../messages';
import { FORM_REQUIREMENTS } from '../../../../utilities/constants';
import { WalletRegistrationErrorBanner } from '../../components/wallet-registration-error-banner';
import { useWalletRegistrationFormContext } from '../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../contexts/wallet-registration-modal-context';

import commonStyles from '../../style.module.css';
import { messages as termsMessages } from './messages';
import { messages as termsLinkMessages } from '../../../../components/messages';
import styles from './style.module.less';

const DEFAULT_WALLET_TERMS_VERSION = '1.0';

export const TermsStep = () => {
  const intl = useIntl();
  const { walletForm, updateWalletForm, submittingRegistration } = useWalletRegistrationFormContext();
  const { terms, nonDefaultTerms } = useWalletRegistrationModalContext();

  const handleTermsChecked = useCallback(
    (checked) => {
      updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.TERMS], checked);
    },
    [updateWalletForm],
  );

  const handleNonDefaultTermsChecked = () => {
    return (checked) => {
      updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.NONDEFAULT_TERMS], checked);
    };
  };

  const { setCanProgress } = useWizard();
  useEffect(() => {
    setCanProgress(walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.TERMS]);
  }, [setCanProgress, walletForm]);

  return (
    <div className={classnames(commonStyles['step-content'], styles['terms-step-content'])}>
      <WalletRegistrationErrorBanner />
      <div className={commonStyles.header}>
        <FormattedMessage {...messages.termsOfService} />
      </div>
      <AcceptTerms
        checkboxLabel={messages.termsCheckbox}
        containerClassName={styles['terms-checkbox-container']}
        disabled={submittingRegistration}
        handleTermsChecked={handleTermsChecked}
        isChecked={walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.TERMS]}
      />
      {terms.map((term) => {
        const hasTermMessage = !!termsMessages[term.termType];
        const messageDerivedLinkText = intl.formatMessage(termsMessages[term.termType] ?? termsLinkMessages.default);
        const hasTermTitle = !!term?.termLink?.title;
        const linkText = !hasTermMessage && hasTermTitle ? term?.termLink?.title : messageDerivedLinkText;
        const version = term.version === DEFAULT_WALLET_TERMS_VERSION ? Versions.default : term.version;
        return (
          <div key={term.termType} className={styles['terms-link-container']}>
            <TermLink
              key={term.termType}
              iconClass={styles['external-link-icon']}
              linkClass={styles['terms-links']}
              termKey={term.termType}
              termText={linkText}
              termUrl={term?.termLink?.url}
              version={version}
            />
          </div>
        );
      })}
      <AcceptTerms
        checkboxLabel={termsMessages.NonDefaultTerms}
        containerClassName={styles['nondefault-terms-checkbox-container']}
        disabled={submittingRegistration}
        handleTermsChecked={handleNonDefaultTermsChecked()}
        isChecked={walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.NONDEFAULT_TERMS]}
      />
      {Object.entries(nonDefaultTerms).map(([labelProviderId, nonDefaultTermList]) => {
        return (
          <React.Fragment key={labelProviderId}>
            {nonDefaultTermList.map((term) => {
              const hasTermMessage = !!termsMessages[term.termType];
              const messageDerivedLinkText = intl.formatMessage(termsMessages[term.termType] ?? termsLinkMessages.default);
              const hasTermTitle = !!term?.termLink?.title;
              const linkText = !hasTermMessage && hasTermTitle ? term?.termLink?.title : messageDerivedLinkText;
              const version = term.version === DEFAULT_WALLET_TERMS_VERSION ? Versions.default : term.version;
              return (
                <div key={term.termType} className={styles['terms-link-container']}>
                  <TermLink
                    key={term.termType}
                    iconClass={styles['external-link-icon']}
                    linkClass={styles['terms-links']}
                    termKey={term.termType}
                    termText={linkText}
                    termUrl={term?.termLink?.url}
                    version={version}
                  />
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};
